import React, { useEffect, useState, useRef } from 'react';
import { useRecoilState } from 'recoil';
import { newEvent, toaster } from '../Recoil/ActiveEvents';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';

const ShowUserList = () => {
  const [notify, setNotify] = useRecoilState(toaster);
  const [eventID] = useRecoilState(newEvent);
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [countInfo, setCountInfo] = useState({ total: 0, today: 0 });
  const [viewMode, setViewMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('view') === 'volunteers' ? 'volunteers' : 'registered';
  });
  const dt = useRef(null);
  const paginatorLeft = <Button type="button" icon="pi pi-refresh" text />;
  const paginatorRight = <Button type="button" icon="pi pi-download" text />;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setViewMode(params.get('view') === 'volunteers' ? 'volunteers' : 'registered');
  }, [location.search]);

  useEffect(() => {
    if (!eventID) return;

    const fetchData = async () => {
      try {
        const apiPath = viewMode === 'volunteers'
          ? '/Registration/GetVolunteer'
          : '/Registration/GetAllRegistrationDetails';

        const eventURI = process.env.REACT_APP_API_URL + apiPath;
        const payload = viewMode === 'volunteers' ? { EventId: eventID } : eventID ;
        const response = await fetch(eventURI, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const result = await response.json();

        if (viewMode === 'volunteers') {
          const volunteerList = result?.Volunteers || (Array.isArray(result) ? result : result ? [result] : []);
          setUsers(volunteerList);

          const todayCount = volunteerList.filter((user) => {
            const createdOn = user.createdOn || user.CreatedOn || user.CreatedDate || user.createdDate;
            return createdOn && moment(createdOn, 'DD-MM-YYYY hh:mm A').isValid() && moment(createdOn, 'DD-MM-YYYY hh:mm A').isSame(moment(), 'day');
          }).length;

          setCountInfo({ total: volunteerList.length, today: todayCount });
        } else {
          const registrations = result?.AllRegistrationDetails || (Array.isArray(result) ? result : result ? [result] : []);
          setUsers(registrations);

          const totalPaid = registrations.filter((item) => !!item.PaymentId);
          const todayCount = totalPaid.filter((user) => {
            const createdOn = user.createdOn || user.CreatedOn;
            return createdOn && moment(createdOn, 'DD-MM-YYYY hh:mm A').isValid() && moment(createdOn, 'DD-MM-YYYY hh:mm A').isSame(moment(), 'day');
          }).length;

          setCountInfo({ total: totalPaid.length, today: todayCount });
        }

        // no filter state needed for this table
      } catch (error) {
        console.error(error);
        setNotify({ showPopup: true, message: 'API failed to load the user list. Please try again.', type: 'error' });
      }
    };

    fetchData();
  }, [eventID, viewMode, setNotify]);

  const fullNameTemplate = (rowData) => `${rowData.FirstName || ''} ${rowData.LastName || ''}`;
  const paymentStatus = (rowData) => (rowData.PaymentId ? 'Paid' : 'Failed');
  const dateTemplate = (rowData) => rowData.createdOn || rowData.CreatedOn || 'N/A';

  const switchView = (mode) => {
    if (viewMode === mode) return;
    setViewMode(mode);
    navigate(`/userView?view=${mode}`, { replace: true });
  };

  const title = viewMode === 'volunteers' ? 'Volunteer Users' : 'Registered Users';
  const totalLabel = viewMode === 'volunteers' ? 'Volunteer Users' : 'Registered Users';
  const todayLabel = viewMode === 'volunteers' ? 'Today’s Volunteer Users' : 'Today’s Registered Users';
  const exportFilename = viewMode === 'volunteers' ? 'volunteers' : 'registered-users';

  return (
    <div className='max-w-[95%] mx-auto px-4 py-12'>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className='text-t-20 font-bold'>{title}</h1>
          <div className='mt-3 flex flex-wrap gap-2'>
            <button
              type='button'
              className={`py-2 px-4 rounded ${viewMode === 'registered' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-800'}`}
              onClick={() => switchView('registered')}
            >
              Registered Users
            </button>
            <button
              type='button'
              className={`py-2 px-4 rounded ${viewMode === 'volunteers' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800'}`}
              onClick={() => switchView('volunteers')}
            >
              Volunteer Users
            </button>
          </div>
        </div>

        <div className='text-right'>
          <h4 className='font-bold'>{totalLabel}: <span className='text-red-600'>{countInfo.total}</span></h4>
          <h4 className='font-bold'>{todayLabel}: <span className='text-red-600'>{countInfo.today}</span></h4>
          <Button label="Export" icon="pi pi-upload" onClick={() => dt.current?.exportCSV()} />
        </div>
      </div>
  {viewMode === 'volunteers' ? (
          <>
             <DataTable
        ref={dt}
        value={Array.isArray(users) ? users : []}
        dataKey={viewMode === 'volunteers' ? 'VolId' : 'RegistrationId'}
        paginator
        rows={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{ minWidth: '50rem' }}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        paginatorLeft={paginatorLeft}
        paginatorRight={paginatorRight}
        exportFilename={exportFilename}
      >
            <Column field="VolId" header="Volunteer ID" style={{ width: '18%' }} filter filterPlaceholder="Search Volunteer ID" />
            <Column field="VolName" header="Name" style={{ width: '18%' }} filter filterPlaceholder="Search Name" />
            <Column field="MobileNo" header="Mobile No" style={{ width: '14%' }} filter filterPlaceholder="Search Mobile No" />
            <Column field="EmailAddress" header="Email ID" style={{ width: '18%' }} filter filterPlaceholder="Search Email" />
            <Column field="Address" header="Address" style={{ width: '18%' }} filter filterPlaceholder="Search Address" />
            <Column field="TshirtSize" header="T-Shirt Size" style={{ width: '12%' }} filter filterPlaceholder="Search T-Shirt Size" />
            <Column body={paymentStatus} header="Payment Status" style={{ width: '12%' }} />
      </DataTable>
          </>
  ) : (
          <>
             <DataTable
        ref={dt}
        value={Array.isArray(users) ? users : []}
        dataKey={viewMode === 'volunteers' ? 'VolId' : 'RegistrationId'}
        paginator
        rows={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{ minWidth: '50rem' }}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        paginatorLeft={paginatorLeft}
        paginatorRight={paginatorRight}
        exportFilename={exportFilename}
      >
       
            <Column header="Registration No" field="RegistrationNo" style={{ width: '18%' }} filter filterPlaceholder="Search Registration No" />
            <Column header="First Name" field="FirstName" style={{ width: '18%' }} filter filterPlaceholder="Search First Name" />
            <Column header="Last Name" field="LastName" style={{ width: '18%' }} filter filterPlaceholder="Search Last Name" />
            <Column header="Name" body={fullNameTemplate} style={{ width: '18%' }} />
            <Column field="Gender" header="Gender" style={{ width: '12%' }} filter filterPlaceholder="Search Gender" />
            <Column field="Emailid" header="Email ID" style={{ width: '18%' }} filter filterPlaceholder="Search Email" />
            <Column field="MobileNo" header="Mobile No" style={{ width: '16%' }} filter filterPlaceholder="Search Mobile No" />
            <Column body={paymentStatus} header="Payment Status" style={{ width: '16%' }} />
            <Column body={dateTemplate} header="Created On" style={{ width: '18%' }} />
            <Column header="Payment Id" field="PaymentId" style={{ width: '18%' }} />
            
      </DataTable>
          </>
  )}
     
    </div>
  );
};

export default ShowUserList;
